import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { websiteAPIService } from '~/api/microsites/website';
import { qrcodeAPIService } from '~/api/qrcode';
import UrlTextField from '~/components/atoms/UrlTextField';
import ContentQRBuilderHeader from '~/components/builder/ContentQRBuilderHeader';
import NameAndOrganizationBlockOldFlow from '~/components/builder/NameAndOrganizationBlockOldFlow';
import PasswordProtectionBlockOldFlow from '~/components/builder/PasswordProtectionBlockOldFlow';
import QrTypeTopCard from '~/components/builder/QrTypeTopCard';
import { SettingsBlock } from '~/components/organisms/editor/SettingsBlock';
import { getStatsFromQrCodeData } from '~/components/organisms/previewBar/model-data/utils';
import {
  QR_CODE_BUILDER_CREATED_ID_PARAM_NAME,
  QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME,
  QRCODE_DYNAMIC_MICROSITE_TYPE,
} from '~/constants/qr-code';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { websiteSlice } from '~/store/reducers/previewScreens/website';
import { getWebsiteReducer } from '~/store/reducers/previewScreens/website/selectors';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { logger } from '~/utils/logger';

import { useQRUrlValidation } from '../hooks/useQRUrlValidation';

const { actions } = websiteSlice;

export default function WebsiteTemplate() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const isEditPage = !!searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);

  const {
    micrositeData,
    websiteInformation,
    stats: { url, scan, isAutogeneratedUrl, isLimitsEnabled },
  } = useSelector(getWebsiteReducer);
  const qrCode = useSelector(getQrCodeReducerData);

  const [urlValue, setUrlValue] = useState('');

  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME) || null;

  useEffect(() => {
    (async () => {
      if (createdId && qrCode.data.microsite?.id) {
        const response = await qrcodeAPIService.getQrCodeById(createdId);

        dispatch(actions.setMicrositeData(response.data.microsite));
        dispatch(actions.setWebsiteInformation(response.data.microsite.url));
        dispatch(actions.setStatsData(getStatsFromQrCodeData(response.data)));
      } else if (qrCode?.data?.id) {
        const response = await websiteAPIService.createWebsiteSite({
          url: 'http://example.com',
          qr_code_id: qrCode.data.id,
        });

        dispatch(actions.setMicrositeData(response.data));
        // dispatch(actions.setWebsiteInformation(response.data.url));
      }
    })();
  }, []);

  useEffect(() => {
    if (websiteInformation) {
      setUrlValue(websiteInformation);
    }
  }, [websiteInformation]);

  const urlValidationParams = useQRUrlValidation(url);

  const handleMicrosite = async () => {
    let response;

    try {
      if (micrositeData) {
        response = await websiteAPIService.updateWebsiteSite({
          body: {
            url: urlValue,
            qr_code_id: qrCode?.data?.id,
          },
          id: micrositeData.id,
        });
      }
      dispatch(actions.setMicrositeData(response.data));
      dispatch(actions.setWebsiteInformation(urlValue));
    } catch (error) {
      logger.error(error.response?.data?.url[0]);
    }
  };

  const handleChangeStatsField = (val) => {
    dispatch(actions.setStats(val));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <ContentQRBuilderHeader />
      <QrTypeTopCard type={QRCODE_DYNAMIC_MICROSITE_TYPE.WEBSITE} />
      <CreateQRContentBlockLayout type="basicInfo">
        <UrlTextField
          urlValue={urlValue}
          fullWidth
          defaultValue={websiteInformation}
          inputProps={{ maxLength: 2000 }}
          onChange={(e) => setUrlValue(e.target.value)}
          onBlur={({ isValid }) => isValid && handleMicrosite()}
          label={t('template0:label1')}
          requireProtocol={false}
        />
      </CreateQRContentBlockLayout>
      <NameAndOrganizationBlockOldFlow onChangeName={(val) => dispatch(actions.setTitle(val))} />
      <PasswordProtectionBlockOldFlow />
      <SettingsBlock
        onChange={handleChangeStatsField}
        urlValidationParams={urlValidationParams}
        scanCount={scan}
        isLimitsEnabled={isLimitsEnabled}
        isAutogeneratedUrl={isAutogeneratedUrl}
        defaultUrlValue={url}
        urlFromBE={qrCode?.data?.uri || ''}
        isEditPage={isEditPage}
      />
    </Box>
  );
}
